import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Query } from '../../../types/tileBuilderTypes';
import { getOp, getOpsByCategory } from '../../../support/transformations/registry';
import ConfigFieldForm from './ConfigFieldForm';

const ITEM_HEIGHT = 36;
const ITEM_PADDING_TOP = 8;
const menuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MODAL_MAX_HEIGHT = '80vh';

// Tab indices — predefined ops are the primary flow; raw JS is the last-resort escape hatch.
const TAB_PREDEFINED = 0;
const TAB_ADVANCED = 1;

interface TransformationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (transformationDef: { type: string; config?: any; logic?: string; queryIndices: number[] }) => void;
  queries: Query[];
  sources: any[]; // object or array
  initialTransformation?: {
    type: string;
    config?: any;
    logic?: string;
    queryIndices: number[];
  };
  availableVariables?: Record<string, string>;
}

const forceScrollableMultilineSx = {
  // Make the input container fill height
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
    alignItems: 'stretch',
  },

  // The multiline input is the textarea and also gets this class
  '& .MuiInputBase-inputMultiline': {
    height: '100% !important',
    overflow: 'auto !important',
    WebkitOverflowScrolling: 'touch',
    fontFamily: 'monospace',
  },
};

// Collect the union of column names across a set of source row arrays.
function detectColumns(sourceArrays: any[]): string[] {
  const keys = new Set<string>();
  const scan = (rows: any) => {
    if (!Array.isArray(rows)) return;
    for (const row of rows.slice(0, 25)) {
      if (row && typeof row === 'object' && !Array.isArray(row)) {
        Object.keys(row).forEach((k) => keys.add(k));
      }
    }
  };
  for (const src of sourceArrays) {
    // A source may itself be an array-of-arrays (multi-source); scan one level deep.
    if (Array.isArray(src) && src.length && Array.isArray(src[0])) src.forEach(scan);
    else scan(src);
  }
  return Array.from(keys);
}

const TransformationModal: React.FC<TransformationModalProps> = ({
                                                                   open,
                                                                   onClose,
                                                                   onSubmit,
                                                                   queries,
                                                                   sources,
                                                                   initialTransformation,
                                                                 }) => {
  const [activeTab, setActiveTab] = useState(TAB_PREDEFINED);
  const [logic, setLogic] = useState('');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [transformationType, setTransformationType] = useState('');
  const [transformationConfig, setTransformationConfig] = useState<any>({});
  const [selectedQueryIndices, setSelectedQueryIndices] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;

    const isScript = initialTransformation?.type === 'script';

    if (isScript) {
      setActiveTab(TAB_ADVANCED);
      setLogic(initialTransformation?.logic || '');
      setTransformationType('');
      setTransformationConfig({});
    } else if (initialTransformation?.type) {
      setActiveTab(TAB_PREDEFINED);
      setTransformationType(initialTransformation.type);
      setTransformationConfig(initialTransformation.config || {});
      setLogic('');
    } else {
      setActiveTab(TAB_PREDEFINED);
      setLogic('');
      setTransformationType('');
      setTransformationConfig({});
    }

    setSelectedQueryIndices(initialTransformation?.queryIndices || []);
    setResult(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleQueryIndexChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedQueryIndices(event.target.value as number[]);
  };

  // Rows the currently-selected sources will feed into this transformation.
  const selectedSourceData = useMemo(
    () => selectedQueryIndices.map((idx) => sources?.[idx] ?? null),
    [sources, selectedQueryIndices],
  );

  const detectedColumns = useMemo(() => detectColumns(selectedSourceData), [selectedSourceData]);

  const dataForScript = useMemo(() => {
    if (!selectedQueryIndices.length) return null;
    const idx = selectedQueryIndices[0];
    return sources?.[idx] ?? null;
  }, [sources, selectedQueryIndices]);

  const rawQueryDataPreview = useMemo(() => {
    try {
      return JSON.stringify(dataForScript ?? null, null, 2);
    } catch {
      return 'Error displaying raw data.';
    }
  }, [dataForScript]);

  const selectedOp = getOp(transformationType);

  const handleEvaluateScript = () => {
    setError(null);
    setResult(null);
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function('data', logic);
      setResult(fn(dataForScript));
    } catch (err: any) {
      setError(err.message || 'Script error');
    }
  };

  const handleSubmit = () => {
    if (activeTab === TAB_ADVANCED) {
      onSubmit({ type: 'script', logic, queryIndices: selectedQueryIndices });
    } else {
      onSubmit({ type: transformationType, config: transformationConfig, queryIndices: selectedQueryIndices });
    }
    onClose();
  };

  const groupedOps = useMemo(() => getOpsByCategory(), []);

  const isSubmitDisabled =
    !selectedQueryIndices.length ||
    (activeTab === TAB_ADVANCED && !logic.trim()) ||
    (activeTab === TAB_PREDEFINED && !transformationType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Define Transformation</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: MODAL_MAX_HEIGHT,
          minHeight: 0,
        }}
      >
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Transformation" />
          <Tab label="Advanced (Raw JS)" />
        </Tabs>

        <FormControl fullWidth>
          <InputLabel id="query-indices-label">Apply to Query Results</InputLabel>
          <Select
            labelId="query-indices-label"
            multiple
            value={selectedQueryIndices}
            onChange={handleQueryIndexChange}
            input={<OutlinedInput id="select-multiple-chip" label="Apply to Query Results" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as number[]).map((val) => (
                  <Chip
                    key={val}
                    label={
                      val < queries.length
                        ? (queries[val]?.model || `Query ${val + 1}`)
                        : `Transformation ${val - queries.length + 1}`
                    }
                  />
                ))}
              </Box>
            )}
            MenuProps={menuProps}
          >
            {queries.map((q, index) => (
              <MenuItem key={`query-${index}`} value={index}>
                Query {index + 1} ({q.model})
              </MenuItem>
            ))}
            {sources.slice(queries.length).map((_, index) => (
              <MenuItem key={`transform-${queries.length + index}`} value={queries.length + index}>
                Transformation {index + 1}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            Select which query results or previous transformation outputs this transformation will operate on.
          </Typography>
        </FormControl>

        {activeTab === TAB_PREDEFINED && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Transformation Type</InputLabel>
              <Select
                value={transformationType}
                label="Transformation Type"
                onChange={(e) => {
                  setTransformationType(e.target.value as string);
                  setTransformationConfig({});
                  setLogic('');
                }}
                MenuProps={menuProps}
              >
                {groupedOps.flatMap((group) => [
                  <ListSubheader key={`cat-${group.category}`}>{group.category}</ListSubheader>,
                  ...group.ops.map((op) => (
                    <MenuItem key={op.type} value={op.type}>
                      {op.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            {selectedOp && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedOp.description}
                  {selectedOp.multiSource && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Select two or more sources above; they are combined in the order selected.
                    </Typography>
                  )}
                </Alert>

                {detectedColumns.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Detected columns: {detectedColumns.join(', ')}
                  </Typography>
                )}

                <ConfigFieldForm
                  fields={selectedOp.configFields}
                  config={transformationConfig}
                  onChange={setTransformationConfig}
                  columns={detectedColumns}
                />
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {activeTab === TAB_ADVANCED && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert severity="warning">
              Raw JavaScript is a last resort. Prefer a predefined transformation where one fits — they are
              safer, configurable, and self-documenting. Only use this tab when no built-in op covers your case.
            </Alert>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              {/* Preview */}
              <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Input Data Preview
                </Typography>

                <TextField
                  value={rawQueryDataPreview}
                  multiline
                  minRows={1}
                  maxRows={9999}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{
                    bgcolor: '#f7f7f7',
                    borderRadius: 1,
                    flex: 1,
                    minHeight: 0,
                    ...forceScrollableMultilineSx,
                  }}
                />
              </Box>

              {/* Script + Result */}
              <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2">
                  <b>Instructions:</b> Write a JavaScript function body that takes <code>data</code> as input.
                </Typography>

                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Transformation Script
                  </Typography>

                  <TextField
                    multiline
                    minRows={1}
                    maxRows={9999}
                    fullWidth
                    value={logic}
                    onChange={(e) => setLogic(e.target.value)}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      ...forceScrollableMultilineSx,
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" onClick={handleEvaluateScript}>
                    Run Script
                  </Button>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Result Preview
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: '#f7f7f7',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      flex: 1,
                      minHeight: 0,
                      overflow: 'auto',
                    }}
                  >
                    {error ? (
                      <Alert severity="error" sx={{ width: '100%' }}>
                        {error}
                      </Alert>
                    ) : result === null ? (
                      <span style={{ color: '#999' }}>No result yet</span>
                    ) : (
                      <span>{JSON.stringify(result, null, 2)}</span>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
          {initialTransformation ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransformationModal;
